import { Provider, Type } from '@angular/core';
import { DashboardAccess } from './dashboard-access';
import { DASHBOARD_ACCESS, DASHBOARD_CONFIG, DASHBOARD_VIEW_STORE, DashboardConfig } from './dashboard-grid.tokens';
import { DashboardGridOptions, DashboardLayout, DEFAULT_DASHBOARD_GRID_OPTIONS } from './layout/dashboard-layout';
import { DashboardState, DashboardStateDefinition, stateFieldsOf } from './state/dashboard-state';
import { DashboardWidgetDefinition } from './widget/dashboard-widget-definition';
import { DashboardViewStore } from './views/dashboard-view';
import { LocalStorageDashboardViewStore } from './views/local-storage-dashboard-view-store';

export interface ProvideDashboardOptions<D extends DashboardStateDefinition, W> {
  /** Stable id; part of the storage key for saved views */
  id: string;
  /** The built-in Default view */
  defaultLayout: DashboardLayout;
  /** Each widget's state must be part of `state`; a widget whose state is missing fails to compile here */
  widgets: W & WidgetsServedBy<W, DashboardState<D>>;
  state?: D;
  grid?: Partial<DashboardGridOptions>;
  /** Defaults to this browser's localStorage */
  store?: Type<DashboardViewStore>;
  /** Defaults to the signed-in Balsa user */
  access?: Type<DashboardAccess>;
}

/** Each widget as is when the dashboard state has what it uses, otherwise a type naming what is missing */
export type WidgetsServedBy<W, State> = {
  [I in keyof W]: W[I] extends DashboardWidgetDefinition<infer _S, infer Uses>
    ? State extends Uses
      ? W[I]
      : { missingDashboardState: Exclude<keyof Uses, keyof State> }
    : W[I];
};

/** Configures one dashboard; add to the providers of the page component that renders `<app-dashboard-grid>`. */
export function provideDashboard<
  // eslint-disable-next-line @typescript-eslint/no-empty-object-type
  const D extends DashboardStateDefinition = {},
  const W extends readonly DashboardWidgetDefinition[] = readonly DashboardWidgetDefinition[],
>(options: ProvideDashboardOptions<D, W>): Provider[] {
  const { id, defaultLayout, widgets, state, grid, store, access } = options;
  const config: DashboardConfig = {
    id,
    defaultLayout,
    grid: { ...DEFAULT_DASHBOARD_GRID_OPTIONS, ...grid },
    widgets: [...widgets],
    state: stateFieldsOf(state ?? {}),
  };
  const providers: Provider[] = [
    { provide: DASHBOARD_CONFIG, useValue: config },
    { provide: DASHBOARD_VIEW_STORE, useClass: store ?? LocalStorageDashboardViewStore },
  ];
  if (access) {
    providers.push({ provide: DASHBOARD_ACCESS, useClass: access });
  }

  return providers;
}
